import { useMemo, useState } from 'react'
import clsx from 'clsx'
import { useValueProps } from '../../hooks'
import type { ValueProps } from '../../types'
import ValueBlock from '../../ValueBlock'
import type { ListFormatProps } from '../../../../components/list-format'
import ListFormat from '../../../../components/list-format'
import type { UploadFile } from '../../../../components/upload/types'
import { getFileIcon } from '../../../../components/upload/UploadFileListCell'
import { BYTES_IN_A_MEGA_BYTE } from '../../../../components/upload/UploadVerify'
import type { FieldUploadProps as FieldUploadProps } from '../../Field/Upload/Upload'
import { transformFiles } from '../../Field/Upload/Upload'
import { formatNumber } from '../../../../components/number-format/NumberUtils'
import { UploadFileLink } from '../../../../components/upload/UploadFileListLink'
import { isAsync } from '../../../../shared/helpers/isAsync'
import withComponentMarkers from '../../../../shared/helpers/withComponentMarkers'

export type ValueUploadProps = ValueProps<Array<UploadFile>> &
  Omit<ListFormatProps, 'value'> &
  Pick<FieldUploadProps, 'download' | 'onFileClick'> & {
    displaySize?: boolean
  }

function Upload(props: ValueUploadProps) {
  const preparedProps = {
    fromExternal: transformFiles,
    ...props,
  }

  const {
    value,
    format,
    className,
    variant = 'text',
    listType,
    download = false,
    displaySize = false,
    onFileClick,
    ...rest
    // @ts-expect-error - strictFunctionTypes
  } = useValueProps(preparedProps)

  const list = useMemo(() => {
    const valueToUse =
      value?.map((uploadFile) => {
        if (!uploadFile) {
          return undefined
        }

        return (
          <UploadFileItem
            key={uploadFile.id}
            uploadFile={uploadFile}
            download={download}
            displaySize={displaySize}
            onFileClick={onFileClick}
          />
        )
      }) || undefined

    if (valueToUse) {
      return (
        <ListFormat
          value={valueToUse}
          format={format}
          variant={variant}
          listType={listType}
        />
      )
    }

    return undefined
  }, [
    value,
    download,
    displaySize,
    onFileClick,
    format,
    variant,
    listType,
  ])

  return (
    <ValueBlock
      className={clsx('dnb-forms-value-upload', className)}
      {...rest}
    >
      {list}
    </ValueBlock>
  )
}

function getSize(size: number) {
  if (!size) {
    return undefined
  }
  // Converts from b (binary) to MB (decimal)
  const sizeInMb = size / BYTES_IN_A_MEGA_BYTE
  return ` (${formatNumber(sizeInMb, {
    decimals: 0,
  })} MB)`
}

withComponentMarkers(Upload, {
  _supportsSpacingProps: true,
})

export default Upload

function UploadFileItem(
  props: { uploadFile: UploadFile } & Pick<
    ValueUploadProps,
    'download' | 'onFileClick' | 'displaySize'
  >
) {
  const {
    uploadFile,
    download = false,
    displaySize = false,
    onFileClick,
  } = props

  const [loading, setLoading] = useState(false)

  const { file, isLoading: fileIsLoading } = uploadFile || {}

  if (!file) {
    return null
  }

  const handleFileClickAsync = async (uploadFile: UploadFile) => {
    setLoading(true)

    try {
      await onFileClick({ fileItem: uploadFile })
    } catch (error) {
      // stop here
    }

    setLoading(false)
  }

  const onFileClickHandler = async () => {
    if (typeof onFileClick === 'function') {
      if (isAsync(onFileClick)) {
        handleFileClickAsync(uploadFile)
      } else {
        onFileClick({ fileItem: uploadFile })
      }
    }
  }

  const imageUrl = file?.size > 0 ? URL.createObjectURL(file) : null

  const text = file.name + (displaySize ? ' ' + getSize(file.size) : '')
  const isLoading = fileIsLoading || loading
  return (
    <span className="dnb-forms-value-upload__item">
      {getFileIcon(file, { isLoading, size: 'medium' }, false)}
      <UploadFileLink
        left="x-small"
        text={text}
        href={imageUrl}
        download={download}
        onClick={onFileClick && onFileClickHandler}
      />
    </span>
  )
}
